import type { ConversationTriage } from '@provision/entity-types';
import { useMutation } from '@provision/graphql-client';
import { useCallback } from 'react';
import {
  CREATE_CONVERSATION_TRIAGE,
  UPDATE_CONVERSATION_TRIAGE,
} from 'src/graphql/entities/conversation-triage/conversation-triage.operations';
import { useNotifications } from 'src/utils/hooks/use-notifications';

const SAVE_TRIAGE_ERROR_MESSAGE =
  'Failed to save Triage Intelligence settings, please try again';

export interface SaveConversationTriagePayload {
  conversationTriageId: string | null;
  instructions: string;
  actionConfig: string;
  llmConfig: string;
  name?: string;
  /** Only honored on create (the partner's first record); set-default owns this on update. */
  isDefault?: boolean;
}

export const useSaveConversationTriage = (partnerId: number | null) => {
  const { showErrorAlert } = useNotifications();

  const onError = useCallback(
    (error: unknown) => {
      console.error(error);
      showErrorAlert(SAVE_TRIAGE_ERROR_MESSAGE);
    },
    [showErrorAlert],
  );

  const [createConversationTriageMutation, { loading: isCreating }] = useMutation<{
    createConversationTriage: ConversationTriage[];
  }>(CREATE_CONVERSATION_TRIAGE, { onError });

  const [updateConversationTriageMutation, { loading: isUpdating }] = useMutation<{
    updateConversationTriage: ConversationTriage[];
  }>(UPDATE_CONVERSATION_TRIAGE, { onError });

  const saveConversationTriage = useCallback(
    async (payload: SaveConversationTriagePayload): Promise<ConversationTriage | null> => {
      const data = {
        instructions: payload.instructions,
        actionConfig: payload.actionConfig,
        llmConfig: payload.llmConfig,
      };

      if (payload.conversationTriageId) {
        const { data: response } = await updateConversationTriageMutation({
          variables: {
            conditions: [
              { attribute: 'id', operator: '=', value: payload.conversationTriageId },
            ],
            data: {
              ...data,
              ...(payload.name !== undefined && { name: payload.name }),
            },
          },
        });
        return response?.updateConversationTriage?.[0] ?? null;
      }

      const { data: response } = await createConversationTriageMutation({
        variables: {
          data: {
            ...data,
            partnerId,
            ...(payload.name !== undefined && { name: payload.name }),
            ...(payload.isDefault !== undefined && { isDefault: payload.isDefault }),
          },
        },
      });
      return response?.createConversationTriage?.[0] ?? null;
    },
    [partnerId, createConversationTriageMutation, updateConversationTriageMutation],
  );

  return {
    saveConversationTriage,
    isSaving: isCreating || isUpdating,
  };
};
